import type { SessionState, ServerMessage, Player } from '../../shared/types';
import { socket } from './socket';

// ─── State ────────────────────────────────────────────────────────────────────

export interface AppState {
  playerId:    string | null;
  username:    string | null;
  roomCode:    string | null;
  session:     SessionState | null;
  myPlayer:    Player | null;
  socketStatus: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
}

const state: AppState = {
  playerId:     localStorage.getItem('lava_player_id'),
  username:     localStorage.getItem('lava_username'),
  roomCode:     null,
  session:      null,
  myPlayer:     null,
  socketStatus: 'disconnected',
};

// ─── Subscribers ──────────────────────────────────────────────────────────────

type Subscriber = (state: AppState) => void;
const subscribers = new Set<Subscriber>();

function notify(): void {
  subscribers.forEach(s => s(state));
}

export function subscribe(fn: Subscriber): () => void {
  subscribers.add(fn);
  fn(state); // immediate call with current state
  return () => subscribers.delete(fn);
}

export function getState(): Readonly<AppState> {
  return state;
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function setUsername(username: string): void {
  state.username = username;
  localStorage.setItem('lava_username', username);
  notify();
}

export function setRoomCode(roomCode: string): void {
  state.roomCode = roomCode;
  notify();
}

// ─── Socket message handler ───────────────────────────────────────────────────

socket.onMessage(handleMessage);
socket.onStatus(status => {
  state.socketStatus = status;
  notify();
});

function handleMessage(msg: ServerMessage): void {
  switch (msg.type) {
    case 'session_state':
      state.session  = msg.state;
      state.myPlayer = state.playerId ? msg.state.players[state.playerId] ?? null : null;
      break;

    case 'player_joined':
      if (state.session) {
        state.session.players[msg.player.id] = msg.player;
      }
      break;

    case 'player_left':
      if (state.session) {
        delete state.session.players[msg.playerId];
      }
      break;

    case 'host_changed':
      if (state.session) {
        for (const p of Object.values(state.session.players)) {
          p.isHost = p.id === msg.newHostId;
        }
      }
      break;

    case 'vote_update':
      if (state.session) {
        for (const [id, vote] of Object.entries(msg.votes)) {
          if (state.session.players[id]) {
            state.session.players[id].vote = vote;
          }
        }
      }
      break;

    case 'volcano_update':
      if (state.session) {
        state.session.volcano = msg.volcano;
        for (const [id, player] of Object.entries(msg.players)) {
          if (state.session.players[id]) {
            state.session.players[id] = player;
          }
        }
      }
      break;

    case 'player_caught':
      if (state.session?.players[msg.playerId]) {
        state.session.players[msg.playerId].status = 'ghost';
      }
      break;

    case 'player_submitted':
      // UI can show submitted state — no state change needed
      break;

    case 'round_results':
    case 'game_starting':
    case 'session_end':
    case 'error':
      // Screens handle these directly via onMessage
      break;
  }

  notify();
}
